'use client';

// Quick Signup - Magic link + OTP passwordless authentication
// Minimal friction: just enter email, then verify with code or click link

import { useState, useRef, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@boty/ui';
import { useAuth } from '@boty/database';
import type { AuthError } from '@boty/database';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslations, useLocale } from 'next-intl';
import { getSessionId, getBotIdFromStorage } from '../../../../lib/session';
import { SocialAuthButtons } from '../../../../features/auth';
import { identifyAuthenticatedUser } from '../../../../lib/analytics';

// Email validation schema
const emailSchema = z.object({
  email: z.string().email(),
});

type EmailFormData = {
  email: string;
};

type PageState = 'email' | 'otp' | 'success';

const OTP_LENGTH = 6;

export default function QuickSignupPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { verifyOtp, user } = useAuth();
  const t = useTranslations('creator.auth.quickSignup');
  const tAuth = useTranslations('creator.auth');
  const locale = useLocale();

  // Get session ID from URL param (from engagement prompt) or localStorage
  const urlSessionId = searchParams.get('sid');
  const sessionId = urlSessionId || getSessionId();

  const isFromBotCreation = !!urlSessionId;

  // Get redirect URL (for returning to protected page after auth)
  const redirectTo = searchParams.get('redirectTo');

  // Validate redirectTo to prevent open redirect attacks
  const isValidRedirect = (url: string): boolean => {
    if (!url.startsWith('/')) return false;
    if (url.startsWith('//')) return false;
    return true;
  };

  // Navigate to the appropriate post-auth destination
  const navigateAfterAuth = () => {
    if (redirectTo && isValidRedirect(redirectTo)) {
      router.push(redirectTo);
      return;
    }
    const botId = getBotIdFromStorage();
    if (botId || sessionId) {
      const params = new URLSearchParams();
      if (botId) params.set('bid', botId);
      if (sessionId) params.set('sid', sessionId);
      router.push(`/${locale}/deploy?${params.toString()}`);
    } else {
      router.push(`/${locale}/dashboard`);
    }
  };

  const [state, setState] = useState<PageState>('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [cooldown, setCooldown] = useState(0);

  // Refs for OTP inputs
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Email form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
  });

  // Redirect if already authenticated (covers OAuth return and page reload)
  useEffect(() => {
    if (user) {
      if (user.id && user.email) {
        identifyAuthenticatedUser(user.id, user.email);
      }
      navigateAfterAuth();
    }
  }, [user, router, locale, redirectTo, sessionId]);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Send OTP via custom API route (localized email via Resend)
  const sendLocalizedOtp = async (
    targetEmail: string
  ): Promise<{ error: AuthError | null }> => {
    const res = await fetch('/api/auth/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: targetEmail,
        locale,
        redirectTo: `${window.location.origin}/auth/callback?locale=${locale}`,
        ...(sessionId && { data: { session_id: sessionId } }),
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return {
        error: {
          message: body?.error?.message || 'Failed to send verification email',
          status: res.status,
        } as AuthError,
      };
    }

    return { error: null };
  };

  // Handle email submission - send OTP
  const onEmailSubmit = async (data: EmailFormData) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await sendLocalizedOtp(data.email);

      if (result.error) {
        setError(result.error.message || t('errors.sendFailed'));
        setIsLoading(false);
        return;
      }

      setEmail(data.email);
      setState('otp');
      setCooldown(60); // 60 second cooldown before resend
      // Store email for verify-email page resend functionality
      localStorage.setItem('pending_verification_email', data.email);
      // Store session_id for bot claiming after verification
      if (sessionId) {
        localStorage.setItem('boty_pending_session_id', sessionId);
      }
    } catch {
      setError(t('errors.sendFailed'));
    }

    setIsLoading(false);
  };

  // Handle OTP input change
  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];

    // Handle paste of full OTP
    if (value.length > 1) {
      const digits = value.slice(0, OTP_LENGTH).split('');
      digits.forEach((digit, i) => {
        if (i < OTP_LENGTH) {
          newOtp[i] = digit;
        }
      });
      setOtp(newOtp);
      // Focus last filled input or submit
      const lastIndex = Math.min(digits.length - 1, OTP_LENGTH - 1);
      otpRefs.current[lastIndex]?.focus();

      // Auto-verify if complete
      if (digits.length === OTP_LENGTH) {
        verifyOtpCode(newOtp.join(''));
      }
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-verify when complete
    if (newOtp.every((d) => d) && newOtp.join('').length === OTP_LENGTH) {
      verifyOtpCode(newOtp.join(''));
    }
  };

  // Handle backspace in OTP
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Verify OTP code
  // Note: session_id metadata is already set during sendOtp
  const verifyOtpCode = async (code: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await verifyOtp(email, code);

      if (result.error) {
        setError(result.error.message || t('errors.invalidCode'));
        setOtp(new Array(OTP_LENGTH).fill(''));
        otpRefs.current[0]?.focus();
        setIsLoading(false);
        return;
      }

      // Identify user in PostHog so person profile gets their email
      if (result.data?.user?.id) {
        identifyAuthenticatedUser(result.data.user.id, email);
      }

      setState('success');
      // Redirect after short delay
      setTimeout(() => {
        // Clean up stored session
        localStorage.removeItem('boty_pending_session_id');
        localStorage.removeItem('pending_verification_email');

        navigateAfterAuth();
      }, 1500);
    } catch {
      setError(t('errors.verifyFailed'));
      setOtp(new Array(OTP_LENGTH).fill(''));
    }

    setIsLoading(false);
  };

  // Resend OTP
  const handleResend = async () => {
    if (cooldown > 0) return;

    setError(null);
    setIsLoading(true);

    try {
      const result = await sendLocalizedOtp(email);

      if (result.error) {
        setError(result.error.message || t('errors.resendFailed'));
      } else {
        setCooldown(60);
      }
    } catch {
      setError(t('errors.resendFailed'));
    }

    setIsLoading(false);
  };

  // Handle OAuth error from callback redirect
  const oauthError = searchParams.get('error') === 'oauth';

  // Email input step
  if (state === 'email') {
    return (
      <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" component="h1" gutterBottom>
          {t(isFromBotCreation ? 'title' : 'titleGeneric')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t(isFromBotCreation ? 'subtitle' : 'subtitleGeneric')}
        </Typography>

        {oauthError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {tAuth('socialAuthError')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {/* Social Auth Buttons */}
        <SocialAuthButtons dividerText={tAuth('orContinueWithEmail')} />

        <Box
          component="form"
          onSubmit={handleSubmit(onEmailSubmit)}
          sx={{ width: '100%' }}
        >
          <Input
            label={tAuth('email')}
            type="email"
            autoComplete="email"
            autoFocus
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message ? tAuth('errors.invalidEmail') : undefined}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting || isLoading ? (
              <CircularProgress size={24} />
            ) : (
              t('continue')
            )}
          </Button>
        </Box>
      </Box>
    );
  }

  // OTP verification step
  if (state === 'otp') {
    return (
      <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('checkEmail')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {t('otpSent', { email })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('otpOrLink')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* OTP Input boxes */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mb: 3,
          }}
        >
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (otpRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              onPaste={(e) => {
                e.preventDefault();
                const pastedData = e.clipboardData.getData('text');
                handleOtpChange(index, pastedData);
              }}
              inputProps={{
                maxLength: index === 0 ? OTP_LENGTH : 1,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  padding: '12px',
                },
                inputMode: 'numeric',
                autoComplete: index === 0 ? 'one-time-code' : 'off',
              }}
              sx={{
                width: 48,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: 2,
                  },
                },
              }}
              disabled={isLoading}
              autoFocus={index === 0}
            />
          ))}
        </Box>

        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('didntReceive')}
        </Typography>

        <Button
          variant="text"
          onClick={handleResend}
          disabled={cooldown > 0 || isLoading}
        >
          {cooldown > 0 ? t('resendIn', { seconds: cooldown }) : t('resend')}
        </Button>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setState('email');
              setOtp(new Array(OTP_LENGTH).fill(''));
              setError(null);
            }}
          >
            {t('changeEmail')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Success state
  return (
    <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'success.main' }}>
        {t('success')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t(isFromBotCreation ? 'redirecting' : 'redirectingGeneric')}
      </Typography>
      <CircularProgress size={24} />
    </Box>
  );
}
